import { type GetRef } from 'antd'
import clsx from 'clsx'
import { parseISO } from 'date-fns'
import {
  forwardRef,
  useCallback,
  useMemo,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type FC
} from 'react'

import { DESCRIPTIONS } from '@/constants/common/message.constants'

import { DatePicker } from '../DatePicker'

const { TimePicker: AntdTimePicker } = DatePicker

type AntdMinuteStep = ComponentProps<typeof AntdTimePicker>['minuteStep']

type Props = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
  /** 時間を設定（ISO形式またはHH:mm:ss形式） */
  value?: string | null
  /** プレースホルダー */
  placeholder?: string
  /** 値変更時のコールバック */
  onChange?: (time: string | null) => void
  /** 無効化 */
  disabled?: boolean
  /** サイズ */
  size?: 'small' | 'middle' | 'large'
  /** 時間フォーマット */
  format?: string
  /** クリア可能 */
  allowClear?: boolean
  /** 分の選択ステップ（Antd TimePicker の minuteStep に転送） */
  minuteStep?: AntdMinuteStep
}

/**
 * 時間選択のコンポーネント
 * - ISO形式またはHH:mm:ss形式の文字列を扱う
 * - 編集可能なIN/OUT時間入力に使用
 */
export const TimePicker: FC<Props> = forwardRef<
  GetRef<typeof AntdTimePicker>,
  Props
>(
  (
    {
      value,
      className,
      placeholder = DESCRIPTIONS.SELECT_TIME,
      format = 'HH:mm',
      allowClear = true,
      onChange,
      disabled,
      size,
      minuteStep,
      ...props
    },
    ref
  ) => {
    // 値をDateオブジェクトに変換
    const dateValue = useMemo(() => {
      if (!value) return null

      // ISO形式の場合はそのままparseISO
      if (value.includes('T') || value.includes('Z')) {
        return parseISO(value)
      }

      // HH:mm:ss形式の場合は今日の日付と組み合わせる
      const today = new Date()
      const [hours, minutes, seconds = '00'] = value.split(':')
      today.setHours(parseInt(hours, 10))
      today.setMinutes(parseInt(minutes, 10))
      today.setSeconds(parseInt(seconds, 10))
      today.setMilliseconds(0)

      return today
    }, [value])

    const handleChange = useCallback(
      (date: Date | null) => {
        if (!onChange) return

        if (!date) {
          onChange(null)
          return
        }

        // HH:mm:ss形式で返す
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        const seconds = date.getSeconds().toString().padStart(2, '0')

        onChange(`${hours}:${minutes}:${seconds}`)
      },
      [onChange]
    )

    return (
      <div {...props} className={clsx(['inline-block', className])}>
        <AntdTimePicker
          ref={ref}
          value={dateValue}
          placeholder={placeholder}
          format={format}
          showNow={false}
          needConfirm={false}
          allowClear={allowClear}
          disabled={disabled}
          size={size}
          minuteStep={minuteStep}
          onChange={handleChange}
        />
      </div>
    )
  }
)
